import React, { useEffect, useState } from 'react';
import PageWrapper from '../../components/PageWrapper';
import { Alert, Box, Button, Chip, Divider, Snackbar, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useProductCreateMutation, useProductListQuery } from '../../services/products';
import { SimpleList } from '../../components/SimpleList';
import { useAuthEffects } from '../../components/Auth';
import AddProductDialog from './components/AddProductDialog';
import { Link as RouterLink } from 'react-router-dom';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

const ProductsListView = () => {
	useAuthEffects();
	const [productCreate, { isSuccess: isProductCreateSuccess, error: productCreateError, reset: productCreateReset }] = useProductCreateMutation();
	const [isAddProductDialogOpen, setIsAddProductDialogOpen] = useState(false);
	const { data: productsListItems, refetch: refetchProductsList } = useProductListQuery();
	const theme = useTheme();
	const { t } = useTranslation();
	const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

	useEffect(() => {
		if (isProductCreateSuccess) {
			setIsAddProductDialogOpen(false);
			refetchProductsList();
		}
	}, [isProductCreateSuccess]);

	const isEmptyView = !productsListItems?.length;

	return <PageWrapper noScroll={isEmptyView} maxWidth='md'>
		{isEmptyView ? <Box flex={1} display='flex' alignItems='center' justifyContent='center' flexDirection='column' gap={4}>
			<Typography textAlign='center' fontSize='1.2rem'>Your products will show up here.</Typography>
			<Button variant='contained' size='large'onClick={() => setIsAddProductDialogOpen(true)}>Add my first product</Button>
		</Box> :
			<Box display='flex' flexDirection='column' gap={2} mt={2}>
				<Box display='flex' alignItems='flex-end' gap={1}>
					<Typography variant='h6'>My Products</Typography>
					<Box flex={1}>
						<Chip label={productsListItems.length} />
					</Box>
					<Button sx={{alignSelf: 'flex-end'}} onClick={() => setIsAddProductDialogOpen(true)}>new product</Button>
				</Box>
				<Divider />
				{productsListItems && <SimpleList 
					items={productsListItems.map((item) => {
						const lastModifiedDate = item.updatedAt ?? item.createdAt;
						const selected = dayjs(lastModifiedDate).isAfter(dayjs().subtract(1, 'minute'));
						const shouldHighlight = dayjs(lastModifiedDate).isAfter(dayjs().subtract(1, 'days'));
						return {
							id: item.id,
							label: item.name,
							selected,
							secondary: shouldHighlight ? t('products.lastUpdated', { date: dayjs(lastModifiedDate).fromNow() }) : undefined,
							highlightSecondary: shouldHighlight,
							imgUrl: item.thumbnailUrl,
							properties: isSmUp ? [...(item.documents.length > 0 ? [{
								el: <Box display='flex' alignItems='center' gap={1}>
									<CloudDownloadIcon sx={{ fontSize: 16, color: 'success.main' }} /> {item.documents.length} {item.documents.length === 1 ? 'file' : 'files'}
								</Box>,
							}] : []), {
								label: item.sku,
							}] : [],
							actions: [{
								icon: <ChevronRightIcon />,
								component: RouterLink,
								to: `/products/${item.id}`,
							}],
						};
					})}
				/>}
			</Box>}
		<AddProductDialog open={isAddProductDialogOpen} onClose={() => setIsAddProductDialogOpen(false)} onSubmit={productCreate} />
		<Snackbar open={Boolean(productCreateError)} autoHideDuration={6000} onClose={productCreateReset}>
			<Alert severity='error'>{(productCreateError as any)?.data?.message}</Alert>
		</Snackbar>
	</PageWrapper>;
};

export default ProductsListView;
